class HaruProduct extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-haru-variants]').textContent);
    this.form = this.querySelector('form');
    this.idInput = this.querySelector('[data-haru-variant-id]');
    this.price = this.querySelector('[data-haru-price]');
    this.unitPrice = this.querySelector('[data-haru-unit-price]');
    this.addButtons = [...this.querySelectorAll('[data-haru-add]')];
    this.addTexts = [...this.querySelectorAll('[data-haru-add-text]')];
    this.buyAgainPrices = [...this.querySelectorAll('[data-haru-buy-again-price]')];
    this.buyAgainVariants = [...this.querySelectorAll('[data-haru-buy-again-variant]')];
    this.sticky = document.querySelector(`[data-haru-sticky="${this.dataset.sectionId}"]`);
    // The sticky bar lives outside this element, so collect from both scopes.
    const scope = (selector) => [
      ...this.querySelectorAll(selector),
      ...(this.sticky ? this.sticky.querySelectorAll(selector) : []),
    ];
    this.addButtons = scope('[data-haru-add]');
    this.addTexts = scope('[data-haru-add-text]');
    this.addPrices = scope('[data-haru-add-price]');
    this.priceNodes = scope('[data-haru-price]');
    // Only the input inside the form is submitted; the sticky one mirrors it.
    this.qtyInputs = scope('[data-haru-qty-input]');
    this.qtyInput = this.querySelector('[data-haru-qty-input]');
    this.currentVariant = this.variants.find((item) => String(item.id) === String(this.idInput.value)) || this.variants[0];
    this.addEventListener('click', this.handleQtyClick);
    this.sticky?.addEventListener('click', this.handleQtyClick);
    this.qtyInputs.forEach((input) => input.addEventListener('input', this.handleQtyInput));
    this.currency = this.dataset.currency;
    this.locale = document.documentElement.lang || 'en-AU';
    this.addEventListener('change', this.handleChange);
    this.syncOptionAvailability(this.readSelection());
    this.observeForm();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleChange);
    this.removeEventListener('click', this.handleQtyClick);
    this.sticky?.removeEventListener('click', this.handleQtyClick);
    this.qtyInputs?.forEach((input) => input.removeEventListener('input', this.handleQtyInput));
    this.observer?.disconnect();
  }

  handleChange = (event) => {
    if (!event.target.matches('[data-haru-option]')) return;
    const changed = Number(event.target.dataset.optionPosition) - 1;
    let selected = this.readSelection();
    let variant = this.matchVariant(selected);

    /*
      The combination just built is not one we can sell - 1kg exists in every
      grind but only Whole Bean is stocked, so picking 1kg while Espresso is
      selected lands on a variant that is real and out of stock.

      Honour the choice just made and move the OTHER options to the nearest
      thing in stock. The alternative - disabling the control they just used -
      makes 1kg unreachable for anyone who picks their grind first, which is
      the order the form is read in.
    */
    if (!variant || !variant.available) {
      const repaired = this.variants.find(
        (item) => item.available && item.options[changed] === selected[changed]
      );
      if (repaired) {
        variant = repaired;
        selected = repaired.options.slice();
        this.optionFields().forEach((field) => {
          field.value = selected[Number(field.dataset.optionPosition) - 1];
        });
      }
    }
    if (!variant) return;

    this.currentVariant = variant;
    this.idInput.value = variant.id;
    if (this.price) this.price.textContent = this.formatMoney(variant.price);
    this.buyAgainPrices.forEach((price) => { price.textContent = this.formatMoney(variant.price); });
    this.buyAgainVariants.forEach((title) => { title.textContent = variant.title.replace('Default Title', ''); });
    this.updateSizePricing(selected, variant);
    this.addButtons.forEach((button) => { button.disabled = !variant.available; });
    this.addTexts.forEach((text) => { text.textContent = variant.available ? this.dataset.addLabel : this.dataset.soldLabel; });
    this.updateSticky(variant);
    this.syncButtonPrice();
    this.syncOptionAvailability(selected);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  };

  optionFields() {
    return [...this.querySelectorAll('[data-haru-option]')];
  }

  readSelection() {
    const selected = [];
    this.optionFields().forEach((field) => {
      selected[Number(field.dataset.optionPosition) - 1] = field.value;
    });
    return selected;
  }

  matchVariant(selection) {
    return this.variants.find(
      (item) => item.options.every((option, index) => option === selection[index])
    );
  }

  /*
    Liquid already disables an option value that is unavailable in EVERY variant
    (value.available), and marks it with the same "(Sold out)" suffix used here.
    What it cannot know is availability given the rest of the selection: Espresso
    is genuinely in stock, just not in 1kg. That is this method's whole job.

    The label is rebuilt from opt.value rather than opt.textContent, because the
    server-rendered text may already carry a "(Sold out)" suffix and re-reading
    it would compound.
  */
  syncOptionAvailability(selection) {
    const soldLabel = this.dataset.soldLabel;
    this.optionFields().forEach((field) => {
      const index = Number(field.dataset.optionPosition) - 1;
      [...field.options].forEach((opt) => {
        const candidate = selection.slice();
        candidate[index] = opt.value;
        const match = this.matchVariant(candidate);
        const sellable = !!(match && match.available);
        // Never disable what is currently chosen - a select whose own selected
        // option is disabled is an invalid control, and arriving on a sold-out
        // variant by URL is legitimate.
        const isCurrent = opt.value === selection[index];
        opt.disabled = !sellable && !isCurrent;
        opt.textContent = sellable ? opt.value : opt.value + ' (' + soldLabel + ')';
      });
    });
  }

  handleQtyClick = (event) => {
    const step = event.target.closest('[data-haru-qty]');
    if (!step || !this.qtyInput) return;
    this.setQuantity((Number(this.qtyInput.value) || 1) + Number(step.dataset.haruQty));
  };

  handleQtyInput = (event) => {
    this.setQuantity(Number(event.target.value));
  };

  setQuantity(value) {
    const next = Math.max(1, Math.round(value) || 1);
    this.qtyInputs.forEach((input) => { input.value = next; });
    this.syncButtonPrice();
  }

  syncButtonPrice = () => {
    // The page price is the selected variant's own price. The button's is a
    // running total, so quantity belongs there and not here.
    if (this.currentVariant) {
      this.priceNodes?.forEach((node) => {
        node.textContent = this.formatMoneyWithoutCurrency(this.currentVariant.price);
      });
    }
    if (!this.addPrices.length || !this.currentVariant) return;
    const quantity = Math.max(1, Number(this.qtyInput?.value) || 1);
    const total = this.currentVariant.price * quantity;
    this.addPrices.forEach((node) => {
      node.hidden = !this.currentVariant.available;
      node.textContent = `\u00b7 ${this.formatMoneyWithoutCurrency(total)}`;
    });
  };

  formatMoney(cents) {
    const formatted = new Intl.NumberFormat(this.locale, {
      style: 'currency',
      currency: this.currency,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 2,
    }).format(cents / 100);
    return formatted;
  }

  formatMoneyWithoutCurrency(cents) {
    return new Intl.NumberFormat(this.locale, {
      style: 'currency',
      currency: this.currency,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 2,
    }).format(cents / 100);
  }

  updateSizePricing(selected, activeVariant) {
    if (!this.unitPrice) return;
    const sizeField = this.querySelector('[data-haru-size-option]');
    const selectedSize = sizeField?.value.toLowerCase().replaceAll(' ', '');
    const match = selectedSize?.match(/^(\d+(?:\.\d+)?)(kg|g)$/);
    if (!match) {
      this.unitPrice.hidden = true;
      return;
    }

    const grams = Number(match[1]) * (match[2] === 'kg' ? 1000 : 1);
    const unitPriceCents = Math.round((activeVariant.price * 100) / grams);
    this.unitPrice.hidden = false;
    this.unitPrice.textContent = this.dataset.unitTemplate.replace(
      '__PRICE__',
      this.formatMoneyWithoutCurrency(unitPriceCents)
    );
  }

  updateSticky(variant) {
    if (!this.sticky) return;
    const variantLabel = this.sticky.querySelector('[data-haru-sticky-variant]');
    if (variantLabel) variantLabel.textContent = variant.title.replace('Default Title', '');
  }

  observeForm() {
    if (!this.sticky || !this.form) return;
    this.observer = new IntersectionObserver(([entry]) => {
      this.sticky.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
    });
    this.observer.observe(this.form);
  }
}

if (!customElements.get('haru-product')) customElements.define('haru-product', HaruProduct);

class HaruCartUpsell extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('.haru-cart-upsell__add');
    this.error = this.querySelector('.haru-cart-upsell__error');
    this.button?.addEventListener('click', this.addCoffee);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.addCoffee);
  }

  addCoffee = async () => {
    if (!this.button || this.button.getAttribute('aria-busy') === 'true') return;

    const cartDrawer = document.querySelector('cart-drawer');
    const cartItems = document.querySelector('cart-items');
    const formData = new FormData();
    formData.append('id', this.dataset.variantId);
    formData.append('quantity', '1');

    if (cartDrawer) {
      formData.append('sections', cartDrawer.getSectionsToRender().map((section) => section.id).join(','));
      formData.append('sections_url', window.location.pathname);
      cartDrawer.setActiveElement(this.button);
    } else if (cartItems) {
      formData.append('sections', cartItems.getSectionsToRender().map((section) => section.section).join(','));
      formData.append('sections_url', window.location.pathname);
    }

    this.button.setAttribute('aria-busy', 'true');
    this.button.disabled = true;
    this.button.querySelector('.loading__spinner')?.classList.remove('hidden');
    this.error.hidden = true;

    try {
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;

      const response = await fetch(routes.cart_add_url, config);
      const cartData = await response.json();
      if (!response.ok || cartData.status) throw new Error(cartData.description || cartData.message);

      const headerCartCount = document.querySelector('.haru-header__cart-count');
      if (headerCartCount) {
        const nextItemCount = (Number.parseInt(headerCartCount.textContent, 10) || 0) + 1;
        headerCartCount.textContent = nextItemCount;
        headerCartCount.closest('a')?.setAttribute(
          'aria-label',
          window.haruCartStrings.viewCart.replace('__COUNT__', nextItemCount)
        );
      }

      if (cartDrawer) {
        cartDrawer.renderContents(cartData);
      } else if (cartItems) {
        cartItems.getSectionsToRender().forEach((section) => {
          const targetSection = document.getElementById(section.id);
          const target = targetSection?.querySelector(section.selector) || targetSection;
          const source = cartData.sections?.[section.section];
          if (target && source) target.innerHTML = cartItems.getSectionInnerHTML(source, section.selector);
        });
      }
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: cartItems ? 'cart-items' : 'haru-cart-upsell',
        productVariantId: this.dataset.variantId,
        cartData,
      });
    } catch (error) {
      this.error.textContent = window.haruCartStrings?.addError || error.message;
      this.error.hidden = false;
    } finally {
      this.button.removeAttribute('aria-busy');
      this.button.disabled = false;
      this.button.querySelector('.loading__spinner')?.classList.add('hidden');
    }
  };
}

if (!customElements.get('haru-cart-upsell')) customElements.define('haru-cart-upsell', HaruCartUpsell);

/*
 * Quick add on product cards. Deliberately a separate element from HaruProduct:
 * the grid has no quantity stepper, no sticky bar and no URL to keep in sync,
 * and folding those branches into HaruProduct would make both harder to read.
 */
class HaruQuickAdd extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('[data-haru-variants]');
    this.form = this.querySelector('form');
    if (!data || !this.form) return;

    this.variants = JSON.parse(data.textContent);
    this.idInput = this.querySelector('[data-haru-variant-id]');
    this.submit = this.querySelector('[data-haru-quick-submit]');
    this.submitText = this.querySelector('[data-haru-quick-text]');
    this.priceNode = this.querySelector('[data-haru-quick-price]');
    this.status = this.querySelector('[data-haru-quick-status]');
    this.toggle = document.querySelector(`[aria-controls="${this.id}"]`);
    this.tile = this.closest('.haru-tile');
    this.currency = this.dataset.currency;
    this.locale = document.documentElement.lang || 'en-AU';
    this.currentVariant =
      this.variants.find((item) => String(item.id) === String(this.idInput.value)) || this.variants[0];

    this.toggle?.addEventListener('click', this.handleToggle);
    this.addEventListener('click', this.handlePanelClick);
    this.addEventListener('change', this.handleChange);
    this.form.addEventListener('submit', this.handleSubmit);
    this.render();
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('click', this.handleToggle);
    this.removeEventListener('change', this.handleChange);
    this.form?.removeEventListener('submit', this.handleSubmit);
  }

  /*
   * Open state lives as a class on the tile, not `hidden` on the panel: on
   * mobile the panel cross-fades over the card text, and display:none cannot
   * be transitioned. `inert` keeps the closed panel out of tab order.
   */
  // The panel carries its own close, since the labelled button is hidden while open.
  handlePanelClick = (event) => {
    if (event.target.closest('[data-haru-quick-close]')) this.handleToggle();
  };

  handleToggle = () => {
    const willOpen = this.inert;
    this.inert = !willOpen;
    this.tile?.classList.toggle('is-quick-open', willOpen);
    this.toggle.setAttribute('aria-expanded', String(willOpen));
    this.toggle.classList.toggle('is-open', willOpen);
    if (willOpen) this.querySelector('select, [data-haru-quick-submit]')?.focus();
  };

  handleChange = (event) => {
    if (!event.target.matches('[data-haru-option]')) return;
    const selected = [];
    this.querySelectorAll('[data-haru-option]').forEach((field) => {
      selected[Number(field.dataset.optionPosition) - 1] = field.value;
    });
    const variant = this.variants.find((item) =>
      item.options.every((option, index) => option === selected[index])
    );
    if (!variant) return;

    this.currentVariant = variant;
    this.idInput.value = variant.id;
    this.render();
  };

  render() {
    if (!this.currentVariant) return;
    const available = this.currentVariant.available;
    this.submit.disabled = !available;
    this.submitText.textContent = available ? this.dataset.addLabel : this.dataset.soldLabel;
    this.priceNode.hidden = !available;
    this.priceNode.textContent = available ? `· ${this.formatMoney(this.currentVariant.price)}` : '';
  }

  handleSubmit = async (event) => {
    event.preventDefault();
    if (!this.currentVariant?.available || this.submit.disabled) return;

    this.submit.disabled = true;
    this.priceNode.hidden = true;
    this.submitText.textContent = this.dataset.addingLabel;
    this.setStatus('');
    try {
      const response = await fetch(`${this.form.action}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: this.currentVariant.id, quantity: 1 }] }),
      });
      if (!response.ok) throw new Error(`Cart responded ${response.status}`);

      const drawer = document.querySelector('haru-cart-drawer');
      if (drawer) {
        await drawer.refreshAndOpen();
      } else {
        await this.refreshCartCount();
      }
      // Collapse the panel so the card is back to rest behind the drawer.
      if (!this.inert) this.toggle?.click();
      this.submit.disabled = false;
      this.priceNode.hidden = false;
      this.submitText.textContent = this.dataset.addLabel;
    } catch (error) {
      // Never leave the customer stuck: fall back to a normal form post.
      this.form.submit();
    }
  };

  async refreshCartCount() {
    const cartUrl = this.form.action.replace(/\/add$/, '.js');
    const cart = await (await fetch(cartUrl, { headers: { Accept: 'application/json' } })).json();
    document.querySelectorAll('.haru-header__cart-count').forEach((node) => {
      node.textContent = cart.item_count;
    });
  }

  setStatus(message) {
    if (this.status) this.status.textContent = message;
  }

  formatMoney(cents) {
    return new Intl.NumberFormat(this.locale, {
      style: 'currency',
      currency: this.currency,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 2,
    }).format(cents / 100);
  }
}

if (!customElements.get('haru-quick-add')) customElements.define('haru-quick-add', HaruQuickAdd);

/*
 * Cart drawer. Contents are always re-rendered by Shopify through the Section
 * Rendering API rather than patched in JS, so the drawer, the free-shipping
 * meter and the totals can never drift out of step with the real cart.
 */
class HaruCartDrawer extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.handleClick);
    document.addEventListener('keydown', this.handleKeydown);
    // A closed drawer was still in the tab order and still read by screen
    // readers: the panel is only pushed off-screen with translateX, which hides
    // it from the eye but not from the keyboard. Twelve phantom stops on every
    // page, and the whole cart announced before the page content.
    if (!this.isOpen) this.setAttribute('inert', '');
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    document.removeEventListener('keydown', this.handleKeydown);
  }

  get isOpen() {
    return this.dataset.drawerOpen === 'true';
  }

  handleClick = (event) => {
    if (event.target.closest('[data-haru-drawer-close]')) {
      this.close();
      return;
    }
    const step = event.target.closest('[data-haru-line-change]');
    if (step) this.changeLine(step.dataset.haruLineChange, step.dataset.haruLineQty);
  };

  handleKeydown = (event) => {
    if (!this.isOpen) return;
    if (event.key === 'Escape') {
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;
    // Keep focus inside the panel while it is modal.
    const focusables = this.querySelectorAll('a[href], button:not([disabled]), input, select, [tabindex]:not([tabindex="-1"])');
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  open() {
    this.lastFocused = document.activeElement;
    this.removeAttribute('inert');
    this.dataset.drawerOpen = 'true';
    this.querySelector('.haru-drawer__scrim')?.removeAttribute('hidden');
    document.documentElement.classList.add('haru-drawer-open');
    this.querySelector('.haru-drawer__close')?.focus();
  }

  close() {
    this.dataset.drawerOpen = 'false';
    this.querySelector('.haru-drawer__scrim')?.setAttribute('hidden', '');
    document.documentElement.classList.remove('haru-drawer-open');
    // Focus goes back to the trigger before the drawer is made inert, so the
    // browser is never left blurring an element inside an inert subtree.
    this.lastFocused?.focus?.();
    this.setAttribute('inert', '');
  }

  async changeLine(line, quantity) {
    this.setBusy(true);
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity: Number(quantity) }),
      });
      if (!response.ok) throw new Error(`Cart responded ${response.status}`);
      await this.refresh();
    } catch (error) {
      window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
    } finally {
      this.setBusy(false);
    }
  }

  setBusy(busy) {
    this.classList.toggle('is-busy', busy);
  }

  async refresh() {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}?sections=haru-cart-drawer`);
    const data = await response.json();
    const markup = data['haru-cart-drawer'];
    if (!markup) return;
    const parsed = new DOMParser().parseFromString(markup, 'text/html');
    const fresh = parsed.querySelector('haru-cart-drawer');
    if (!fresh) return;

    const wasOpen = this.isOpen;
    this.innerHTML = fresh.innerHTML;
    // innerHTML replacement resets the scrim, so restore the visible state.
    if (wasOpen) {
      this.querySelector('.haru-drawer__scrim')?.removeAttribute('hidden');
    } else {
      this.querySelector('.haru-drawer__scrim')?.setAttribute('hidden', '');
    }
    this.syncCartCount();
  }

  async syncCartCount() {
    const root = window.Shopify?.routes?.root || '/';
    const cart = await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();
    document.querySelectorAll('.haru-header__cart-count').forEach((node) => {
      node.textContent = cart.item_count;
    });
  }

  async refreshAndOpen() {
    await this.refresh();
    this.open();
  }
}

if (!customElements.get('haru-cart-drawer')) customElements.define('haru-cart-drawer', HaruCartDrawer);

/*
  Haru logo inside the Shopify Forms popup.

  The app renders in a shadow root, so the logo cannot be added from Liquid or
  CSS - there is no selector that reaches in and no variable for an image at the
  top. It has to be inserted into the shadow tree after the app builds it.

  The source is read from the header's own logo rather than hardcoded, so if the
  merchant swaps the logo in the theme editor this follows it automatically.

  Fragile by the same measure as the CSS variables: it depends on the app's
  internal class names, and a Forms update could rename _formHeader_ and quietly
  drop the logo. It fails silently and leaves the form working.
*/
(() => {
  const LOGO_WIDTH = 150;
  let done = false;

  const inject = () => {
    if (done) return true;
    const host = document.querySelector('shopify-forms-embed');
    const root = host && host.shadowRoot;
    if (!root) return false;

    const header = root.querySelector('[class*="_formHeader_"]');
    if (!header) return false;
    if (root.querySelector('[data-haru-forms-logo]')) { done = true; return true; }

    // A trimmed copy of the lockup. The theme's haru-logo.png carries 108px of
    // whitespace above and 109px below - 57% of its height - so at any given
    // width the mark rendered far smaller than the box it sat in.
    const source = document.querySelector('[data-haru-forms-mark]');
    if (!source) return false;

    /*
      A stylesheet inside the shadow root is the only way to reach what the app's
      CSS variables do not cover: it exposes no variable for button typography,
      for field padding on the x axis alone, or for the disclaimer size.

      Selectors use [class*=] because the app's class names carry a build hash
      (_formSubmitButton_jnbzb_106) that changes between releases. The stable
      part is the prefix.
    */
    const style = document.createElement('style');
    style.textContent = [
      '[data-haru-forms-logo]{display:block;width:' + LOGO_WIDTH + 'px;height:auto;margin:0 auto 34px;}',
      // Fields sit on a 22px inset everywhere else on the site; the app uses 15px.
      '[class*="_formInputField_"]{padding-left:22px!important;padding-right:22px!important;}',
      '[class*="_formInputFieldLabel_"]{left:22px!important;}',
      // Primary buttons are uppercase 12/600 with 1px tracking - SEND, QUICK ADD,
      // EMAIL ME. The app renders sentence case at 16/400.
      '[class*="_formSubmitButton_"]{font-size:12px!important;font-weight:600!important;letter-spacing:1px!important;text-transform:uppercase!important;}',
      // Same arrow and same 4px nudge as .haru-card__arrow and .haru-textlink__arrow.
      '[class*="_formSubmitButton_"]::after{content:"\\2192";display:inline-block;margin-left:8px;transition:transform 150ms ease;}',
      '[class*="_formSubmitButton_"]:hover::after{transform:translateX(4px);}',
      /*
        The circle wipe from .haru-btn: a 34px dot pinned left of centre that
        scales to 30x, so the lighter navy sweeps across rather than the whole
        button switching colour. Built on ::before because ::after already
        carries the arrow. isolation + z-index:-1 keep it behind the label, and
        overflow:hidden clips it to the pill.
      */
      '[class*="_formSubmitButton_"]{position:relative!important;overflow:hidden!important;isolation:isolate!important;transition:transform 260ms ease!important;}',
      '[class*="_formSubmitButton_"]::before{content:"";position:absolute;z-index:-1;left:8px;top:50%;width:34px;height:34px;margin-top:-17px;border-radius:50%;background:#254876;transform:scale(0);transform-origin:center;transition:transform 520ms cubic-bezier(0.33,0,0.2,1);}',
      '[class*="_formSubmitButton_"]:hover::before,[class*="_formSubmitButton_"]:focus-visible::before{transform:scale(30);}',
      // The app fades the button on hover; the site lifts it 2px instead.
      '[class*="_formSubmitButton_"]:hover{filter:none!important;transform:translateY(-2px)!important;}',
      // 10px is below the 12px floor of the type scale, and full navy is too
      // strong for fine print.
      '[class*="_formDisclaimer_"],[class*="_formDisclaimer_"] p{font-size:12px;line-height:1.4;color:rgba(28,43,58,0.65);}',
      // The popup inherits letter-spacing from the page body (0.6px), so every
      // size gets the same tracking. The site varies it by role: 0.0143em on
      // headings, 0.0375em on body. At 24px that is 0.34px, not 0.6px.
      '[class*="_textHeading_"]{letter-spacing:0.34px!important;line-height:1.15!important;}',
      // Site body copy runs at 1.65 line-height and navy at 75%; the app ships
      // 1.25 and full-strength navy, which reads tight and heavy next to it.
      '[class*="_textBody_"] p{line-height:1.6!important;color:rgba(28,43,58,0.75)!important;}',
      /*
         The policy links read as plain text. In consent copy they are there to
         be findable if wanted, not to pull attention away from the offer - so
         they inherit the disclaimer's colour and carry no underline at rest.
         The underline returns on hover and on keyboard focus, which keeps them
         discoverable without adding any static noise.
      */
      '[class*="_formDisclaimer_"] a{color:inherit;text-decoration:none;}',
      '[class*="_formDisclaimer_"] a:hover,[class*="_formDisclaimer_"] a:focus-visible{text-decoration:underline;text-underline-offset:2px;}',
      '[data-haru-accent]{color:#9C7E33;}',
      // Teaser tab in brand gold rather than the app's white card.
      '[class*="_teaserContainer_"]{background:#C8A96E!important;}',
      '[class*="_teaserHeading_"]{color:#1C2B3A!important;font-weight:600!important;letter-spacing:1px!important;text-transform:uppercase!important;}',
      '[class*="_teaserContainer_"] [class*="_formCloseButton_"] svg{fill:#1C2B3A!important;}',
      // Fine print matches .haru-footer__consent: 12px at 0.6px tracking.
      '[class*="_formDisclaimer_"],[class*="_formDisclaimer_"] p{letter-spacing:0.6px!important;}',
      // Site inputs carry no tracking at all - .haru-footer__field computes to
      // normal. The popup was inheriting 0.6px from the page body.
      '[class*="_formInputField_"],[class*="_formInputFieldLabel_"]{letter-spacing:normal!important;}'
    ].join('');
    root.appendChild(style);

    const logo = document.createElement('img');
    logo.setAttribute('data-haru-forms-logo', '');
    // Decorative: the heading already names the offer, and the form has its own
    // accessible name, so announcing the wordmark again is noise.
    logo.setAttribute('aria-hidden', 'true');
    logo.alt = '';
    logo.src = source.currentSrc || source.src;
    header.parentNode.insertBefore(logo, header);

    /*
      The offer takes the hero's accent treatment: .haru-hero__accent renders
      "Korean Soul" in --haru-gold-display, so the phrase that carries the
      meaning is gold and the rest stays navy. Same idea here.
    */
    const heading = root.querySelector('[class*="_textHeading_"]');
    if (heading && !heading.querySelector('[data-haru-accent]')) {
      const m = heading.textContent.match(/^(\s*\d+%\s*off)(.*)$/i);
      if (m) {
        heading.innerHTML =
          '<span data-haru-accent>' + m[1] + '</span>' +
          m[2].replace(/&/g,'&amp;').replace(/</g,'&lt;');
      }
    }

    /*
      The disclaimer names the privacy policy and terms of service but ships as
      flat text. The app's own consent field accepts links, but it sits behind
      an admin iframe, so they are added here instead.

      Matches on the phrases, so if the disclaimer copy is rewritten the links
      simply stop appearing rather than landing in the wrong place.
    */
    const disclaimer = root.querySelector('[class*="_formDisclaimer_"] p');
    if (disclaimer && !disclaimer.querySelector('a')) {
      const escaped = disclaimer.textContent
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      let markup = escaped;
      const link = (phrase, href) => {
        const at = markup.indexOf(phrase);
        if (at === -1) return false;
        markup = markup.slice(0, at)
          + '<a href="' + href + '" target="_blank" rel="noopener">' + phrase + '</a>'
          + markup.slice(at + phrase.length);
        return true;
      };
      link('privacy policy', '/policies/privacy-policy');
      // Longest first: matching bare "terms" against "terms of service" would
      // link only the first word and leave "of service" hanging outside it.
      link('terms of service', '/policies/terms-of-service')
        || link('terms and conditions', '/policies/terms-of-service')
        || link('terms', '/policies/terms-of-service');
      if (markup !== escaped) disclaimer.innerHTML = markup;
    }

    done = true;
    return true;
  };

  if (inject()) return;

  // The popup mounts on a delay and builds its shadow tree asynchronously, so
  // poll rather than observe - MutationObserver on the document never sees
  // changes that happen inside a shadow root.
  const started = Date.now();
  const timer = setInterval(() => {
    if (inject() || Date.now() - started > 45000) clearInterval(timer);
  }, 400);
})();

/*
  Discount code copy button ------------------------------------------------

  The Forms app renders the code with a copy button whose entire implementation
  is `navigator.clipboard.writeText(code)`. It has NO success state: no label
  change, no icon swap, no toast. The only response to a click is the app's own
  :active brightness filter while the button is held down. It copies, and gives
  no evidence of it, so it reads as broken.

  The bundle is served from Shopify's CDN and cannot be patched, so the feedback
  is added here instead. Two things happen on click:

    1. A second copy attempt with a real fallback. The app's call can reject
       (clipboard permission denied, older Safari) and it swallows the rejection
       - so a hidden textarea + execCommand runs when the promise fails, and the
       code is selected for a manual copy when even that fails.
    2. A "Copied" pill above the button, in the theme's LABEL spec (11px/600,
       uppercase, 1.2px tracking) so it matches every other pill on the site.

  The block also primes the discount into the session the moment the code is
  issued, so copying is a convenience rather than a requirement - see below.

  Deliberately separate from the styling block above: that one waits for
  _formHeader_, which only exists in the pre-submit form, and stops polling once
  it succeeds. The copy button lives in the success banner that replaces it, so
  this needs its own hook that survives the swap. The listener is delegated on
  the shadow root, so it costs one binding and catches the button whenever the
  banner renders.

  Fragile in the same way as everything else here - it keys off the app's
  hashed class names via their stable prefix. If Forms renames them the pill
  stops appearing and the app's own button keeps working.
*/
(() => {
  const HOLD_MS = 1800;
  // execCommand is deprecated but remains the only synchronous copy path, and
  // it is reached only when the async API has already failed.
  const legacyCopy = (text) => {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      // Off-screen rather than display:none - a hidden field cannot be selected.
      ta.style.cssText = 'position:fixed;top:0;left:-9999px;opacity:0;';
      document.body.appendChild(ta);
      ta.select();
      ta.setSelectionRange(0, text.length);
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch (e) {
      return false;
    }
  };

  const copy = (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text)
        .then(() => true)
        .catch(() => legacyCopy(text));
    }
    return Promise.resolve(legacyCopy(text));
  };

  // Last resort: leave the code selected so the keyboard shortcut works.
  const selectText = (el) => {
    try {
      const range = document.createRange();
      range.selectNodeContents(el);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    } catch (e) { /* selection is a nicety, never a failure path */ }
  };

  let done = false;

  const attach = () => {
    if (done) return true;
    const host = document.querySelector('shopify-forms-embed');
    const root = host && host.shadowRoot;
    if (!root) return false;

    const style = document.createElement('style');
    style.setAttribute('data-haru-forms-copy', '');
    style.textContent = [
      // The pill sits above the button, so nothing between it and the banner
      // edge may clip it.
      '[class*="_formBannerContainer_"]{overflow:visible!important;}',
      // The app's button is `all:unset`, which resets position to static.
      '[class*="_copyToClipboardButton_"]{position:relative!important;}',
      '[class*="_copyToClipboardButton_"]::after{content:"Copied";position:absolute;bottom:calc(100% + 6px);left:50%;transform:translate(-50%,4px);padding:5px 10px;border-radius:999px;background:#1C2B3A;color:#FAF8F4;font-size:11px;font-weight:600;letter-spacing:1.2px;text-transform:uppercase;white-space:nowrap;opacity:0;pointer-events:none;transition:opacity 160ms ease,transform 160ms ease;}',
      '[class*="_copyToClipboardButton_"][data-haru-copied]::after{opacity:1;transform:translate(-50%,0);}',
      // Maroon, matching 온 ON, for the path where the clipboard refused us.
      '[class*="_copyToClipboardButton_"][data-haru-copied="manual"]::after{content:"' + (/Mac|iPhone|iPad/.test(navigator.platform || '') ? 'Press \\2318 C' : 'Press Ctrl+C') + '";background:#6B2735;}',
      '@media (prefers-reduced-motion: reduce){[class*="_copyToClipboardButton_"]::after{transition:none;}}'
    ].join('');
    root.appendChild(style);

    // The app gives the button no accessible confirmation either, and a CSS
    // pseudo-element is invisible to assistive tech.
    const live = document.createElement('span');
    live.setAttribute('data-haru-forms-live', '');
    live.setAttribute('aria-live', 'polite');
    live.setAttribute('role', 'status');
    live.style.cssText = 'position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0;';
    root.appendChild(live);

    /*
      Auto-apply. /discount/<code> is Shopify's own route for putting a code in
      the session; fetching it in the background does the same thing a click on
      a discount link would, so the 10% is already on the cart before the
      customer reaches checkout and the copy button becomes optional.

      Guarded on the existing cookie: only one discount code can be held at a
      time, so a customer who arrived on someone else's link keeps theirs rather
      than having it silently replaced by the welcome code.

      A MutationObserver is correct here even though the block above warns
      against one - that warning is about observing the *document*, which never
      sees inside a shadow tree. Observing the shadow root itself does.
    */
    let primed = /(^|;\s*)discount_code=/.test(document.cookie);
    const prime = () => {
      if (primed) return true;
      const held = root.querySelector('[class*="_copyToClipboardText_"]');
      const code = held && held.textContent.trim();
      if (!code) return false;
      primed = true;
      // Fire and forget: the cart reflects it on the next read either way, and
      // a failure here still leaves the customer the code on screen.
      fetch('/discount/' + encodeURIComponent(code), { credentials: 'same-origin' })
        .catch(() => {});
      return true;
    };

    if (!prime()) {
      const watcher = new MutationObserver(() => { if (prime()) watcher.disconnect(); });
      watcher.observe(root, { childList: true, subtree: true });
    }

    root.addEventListener('click', (ev) => {
      // The click usually lands on the svg or its path, not the button.
      const target = ev.target;
      const btn = target && target.closest
        && target.closest('button[class*="_copyToClipboardButton_"]');
      if (!btn) return;

      const label = btn.querySelector('[class*="_copyToClipboardText_"]');
      const code = (label ? label.textContent : btn.textContent).trim();
      if (!code) return;

      copy(code).then((ok) => {
        if (!ok && label) selectText(label);
        btn.setAttribute('data-haru-copied', ok ? 'yes' : 'manual');
        live.textContent = ok
          ? 'Discount code ' + code + ' copied to clipboard.'
          : 'Press the copy shortcut to copy discount code ' + code + '.';
        clearTimeout(btn.haruCopyTimer);
        btn.haruCopyTimer = setTimeout(() => {
          btn.removeAttribute('data-haru-copied');
          live.textContent = '';
        }, HOLD_MS);
      });
    });

    done = true;
    return true;
  };

  if (attach()) return;

  // Same reason as the block above: a MutationObserver on the document never
  // sees the shadow tree being built.
  const started = Date.now();
  const timer = setInterval(() => {
    if (attach() || Date.now() - started > 45000) clearInterval(timer);
  }, 400);
})();
